/**
 * PII masking / privacy controls for the heavy rrweb replay flux
 * (Epic 4 — Confidentialité, story 4.1).
 *
 * The heavy replay records the live DOM. By default rrweb captures the *values*
 * users type into inputs (passwords aside, which it already masks) and the
 * visible text. On any real-data surface that is PII — architecture decision D7
 * and PRD Open Q5 require masking to be enabled **before** any real data flows.
 * This module is that gate.
 *
 * SAFE-BY-DEFAULT: with no `privacy` config at all, ALL input values are masked
 * in the replay (`maskAllInputs: true`). A surface that is known to be free of
 * sensitive input can opt out with `privacy: { maskAllInputs: false }`.
 *
 * The defaults map the SDK's small `PrivacyOptions` bag onto rrweb's native
 * masking knobs, and expose three conventional CSS classes so a host page can
 * mark sensitive nodes declaratively without writing custom functions:
 *   - `observ-mask`   → mask the TEXT of the element (rrweb `maskTextClass`)
 *   - `observ-block`  → drop the element from the replay entirely (`blockClass`)
 *   - `observ-ignore` → record the element but ignore its user input (`ignoreClass`)
 *
 * Channel scope: this governs the HEAVY flux only (rrweb → `/v1/replay`). Masking
 * the LIGHT semantic-event text (`element.text` on `observ.session.click`) is a
 * separate concern handled by a later story (4.2); the two channels are never
 * crossed (enforcement rule).
 */
import type { PrivacyOptions } from './types.js'

/** Default class marking elements whose TEXT is masked in the replay (rrweb `maskTextClass`). */
export const DEFAULT_MASK_TEXT_CLASS = 'observ-mask'
/** Default class marking elements dropped entirely from the replay (rrweb `blockClass`). */
export const DEFAULT_BLOCK_CLASS = 'observ-block'
/** Default class marking inputs whose user input events are ignored (rrweb `ignoreClass`). */
export const DEFAULT_IGNORE_CLASS = 'observ-ignore'

/**
 * The subset of rrweb `recordOptions` that govern masking. Intentionally a flat,
 * fully-resolved shape (no `undefined` for the always-present fields) so the
 * recorder can spread it straight into `record({ ... })` and tests can assert it.
 */
export interface ReplayMaskingOptions {
  /** Mask the value of every `<input>`/`<textarea>`/`<select>` in the replay. */
  maskAllInputs: boolean
  /** Mask the text of elements carrying this class. */
  maskTextClass: string
  /** Drop elements carrying this class from the replay. */
  blockClass: string
  /** Ignore user input on elements carrying this class. */
  ignoreClass: string
  /**
   * When `maskAllText` is on, a selector matching every element so rrweb masks
   * ALL text node content. Omitted otherwise (only class-scoped text masking).
   */
  maskTextSelector?: string
}

/**
 * Resolve the public {@link PrivacyOptions} into the rrweb masking options,
 * applying the safe-by-default policy. Pure (no DOM/global access) so it is
 * trivially unit-testable and reusable from `init()`.
 */
export function resolveReplayMasking(privacy?: PrivacyOptions): ReplayMaskingOptions {
  const resolved: ReplayMaskingOptions = {
    // Safe-by-default: inputs are masked unless the host *explicitly* opts out.
    maskAllInputs: privacy?.maskAllInputs ?? true,
    maskTextClass: privacy?.maskTextClass ?? DEFAULT_MASK_TEXT_CLASS,
    blockClass: privacy?.blockClass ?? DEFAULT_BLOCK_CLASS,
    ignoreClass: privacy?.ignoreClass ?? DEFAULT_IGNORE_CLASS,
  }
  // Full-text masking is opt-in: it heavily degrades replay usefulness, so it is
  // off by default and only class-scoped text masking applies. '*' tells rrweb
  // to treat every element as a masked-text element.
  if (privacy?.maskAllText) resolved.maskTextSelector = '*'
  return resolved
}
